import React, { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import {
  Users,
  Building2,
  Clock,
  HeartPulse,
  ChevronLeft,
  ChevronRight,
  Info,
  Download,
  MapPin,
  Stethoscope,
  UserCheck,
  LayoutGrid,
} from 'lucide-react'
import type { ProviderPerformanceDetail, UserContext } from '@/lib/equip-types'
import { useCurrentQuarter } from '@/hooks/equip/useCurrentQuarter'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'

interface ProviderProfileBarProps {
  performance: ProviderPerformanceDetail | null
  userContext: UserContext | null
  selectedQuarter: { year: number; quarter: number }
  onQuarterChange: (year: number, quarter: number) => void
  onTotalPatientsClick: () => void
  onAcoPatientsClick?: () => void
  onExpandChange?: (isExpanded: boolean) => void
  onAskQuestion?: () => void
  onDownloadReport?: () => void
  expandedContent?: React.ReactNode
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getProviderTypeLabel(providerType: string | null | undefined): string {
  if (!providerType) return 'Unknown'
  const typeMap: Record<string, string> = {
    'PC Physician': 'PC Physician',
    'PC Psychiatrist': 'Psychiatrist',
    'Physician Assistant': 'PA',
    'Nurse Practitioner': 'NP',
    'Certified Nurse Specialist': 'CNS',
    Psychologist: 'Psychologist',
    'Physician Psychiatrist': 'Psychiatrist',
    APM: 'APM',
  }
  return typeMap[providerType] || providerType
}

function getPartTimeStatus(employmentStatus: string | null | undefined, avgDaysPerWeek: number | null | undefined): string {
  if (employmentStatus === 'Part Time') {
    if (avgDaysPerWeek != null) {
      return `${avgDaysPerWeek.toFixed(1)}d/wk`
    }
    return 'Part Time'
  }
  return 'Full Time'
}

export const ProviderProfileBar: React.FC<ProviderProfileBarProps> = ({
  performance,
  userContext: _userContext,
  selectedQuarter,
  onQuarterChange,
  onTotalPatientsClick,
  onAcoPatientsClick,
  onExpandChange,
  onAskQuestion,
  onDownloadReport,
  expandedContent,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Current quarter from backend API (cached, gated by EquipScopeContext loading)
  const currentQ = useCurrentQuarter()

  const providerName = performance?.providerName || 'Unknown Clinician'
  const providerType = performance?.bonusEligibleType || 'Unknown'
  const totalPatients = performance?.totalPatientCount ?? 0
  const facilities = performance?.facilityCount ?? 0
  const partTimeStatus = getPartTimeStatus(performance?.employmentStatus, performance?.avgDaysPerWeek)
  const acoPatients = performance?.acoPatientCount ?? 0

  // Determine eligibility for the selected quarter.
  // Current quarter: use eligibleAtQuarterStart (quarter is ongoing — end not yet known).
  // Past quarters: use eligibleAtQuarterEnd (final eligibility determination).
  const isCurrentQuarter =
    selectedQuarter.year === currentQ.year && selectedQuarter.quarter === currentQ.quarter
  const isEligible = performance
    ? isCurrentQuarter
      ? performance.eligibleAtQuarterStart
      : performance.eligibleAtQuarterEnd
    : null

  const handleToggle = () => {
    const next = !isExpanded
    setIsExpanded(next)
    onExpandChange?.(next)
  }

  const handlePreviousQuarter = () => {
    let { year, quarter } = selectedQuarter
    quarter--
    if (quarter < 1) {
      quarter = 4
      year--
    }
    onQuarterChange(year, quarter)
  }

  const handleNextQuarter = () => {
    let { year, quarter } = selectedQuarter
    quarter++
    if (quarter > 4) {
      quarter = 1
      year++
    }
    onQuarterChange(year, quarter)
  }

  return (
    <div className="rounded-xl border border-border bg-background overflow-hidden">
      {/* Main bar row — clickable to expand/collapse */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Left section: Avatar, name, inline metadata */}
        <div className="flex items-center gap-3">
          {/* Expand/collapse chevron — left of avatar, matches measure chevron behavior */}
          <motion.div
            animate={{
              opacity: isHovered || isExpanded ? 1 : 0.3,
              rotate: isExpanded ? 90 : 0,
              scale: isHovered && !isExpanded ? 1.4 : 1,
            }}
            transition={{ duration: 0.2 }}
            className="shrink-0"
          >
            <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
          </motion.div>

          {/* Avatar with hover popover showing extended profile metadata */}
          <HoverCard openDelay={300} closeDelay={100}>
            <HoverCardTrigger asChild>
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0 cursor-default">
                {getInitials(providerName)}
              </div>
            </HoverCardTrigger>
            <HoverCardContent
              className="w-64 p-3"
              side="right"
              align="start"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
                className="space-y-2"
              >
                <p className="text-xs font-semibold text-foreground mb-2">{providerName}</p>
                {performance?.leadName && (
                  <div className="flex items-start gap-2">
                    <UserCheck className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Lead</p>
                      <p className="text-xs text-foreground">{performance.leadName}</p>
                    </div>
                  </div>
                )}
                {performance?.supervisingPhysicianName && (
                  <div className="flex items-start gap-2">
                    <Stethoscope className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Supervising Physician</p>
                      <p className="text-xs text-foreground">{performance.supervisingPhysicianName}</p>
                    </div>
                  </div>
                )}
                {performance?.careTeam && (
                  <div className="flex items-start gap-2">
                    <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Care Team</p>
                      <p className="text-xs text-foreground">{performance.careTeam}</p>
                    </div>
                  </div>
                )}
                {performance?.employeeState && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">State</p>
                      <p className="text-xs text-foreground">{performance.employeeState}</p>
                    </div>
                  </div>
                )}
                {!performance?.leadName && !performance?.supervisingPhysicianName && !performance?.careTeam && !performance?.employeeState && (
                  <p className="text-xs text-muted-foreground italic">No additional profile data</p>
                )}
              </motion.div>
            </HoverCardContent>
          </HoverCard>

          {/* Name and metadata */}
          <div>
            {/* Name with inline badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-sm font-semibold text-foreground">{providerName}</h1>
              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary uppercase tracking-wider">
                {getProviderTypeLabel(providerType)}
              </span>
              {isEligible !== null && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                        className={
                          isEligible
                            ? 'inline-flex px-2 py-0.5 rounded-full text-[9px] font-medium bg-success/15 text-success uppercase tracking-wider'
                            : 'inline-flex px-2 py-0.5 rounded-full text-[9px] font-medium bg-destructive/15 text-destructive uppercase tracking-wider'
                        }
                      >
                        {isEligible ? 'Eligible' : 'Ineligible'}
                      </motion.span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isEligible
                        ? 'This clinician is considered eligible based on meeting productivity requirements'
                        : 'This clinician is considered ineligible based on not meeting productivity requirements'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Inline metadata with separators */}
            <div className="flex items-center gap-2 mt-1 flex-wrap text-sm">
              {/* Patients */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onTotalPatientsClick()
                  if (!isExpanded) {
                    setIsExpanded(true)
                    onExpandChange?.(true)
                  }
                }}
                className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Users className="w-4 h-4" />
                <span className="font-semibold text-foreground">{totalPatients}</span>
                <span>patients</span>
              </button>

              <span className="text-muted-foreground/60">|</span>

              {/* ACO Patients */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onAcoPatientsClick?.()
                  if (!isExpanded) {
                    setIsExpanded(true)
                    onExpandChange?.(true)
                  }
                }}
                className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <HeartPulse className="w-4 h-4" />
                <span className="font-semibold text-foreground">{acoPatients}</span>
                <span>ACO patients</span>
              </button>

              <span className="text-muted-foreground/60">|</span>

              {/* Facilities */}
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Building2 className="w-4 h-4" />
                <span className="font-semibold text-foreground">{facilities}</span>
                <span>{facilities === 1 ? 'facility' : 'facilities'}</span>
              </span>

              <span className="text-muted-foreground/60">|</span>

              {/* Schedule */}
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-foreground font-semibold">{partTimeStatus}</span>
                {partTimeStatus !== 'Full Time' && (
                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                    Part-Time
                  </span>
                )}
              </span>

            </div>
          </div>
        </div>

        {/* Right section: Ask a Question + Quarter selector */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Download Report button */}
          {onDownloadReport && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDownloadReport()
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              <Download className="h-3.5 w-3.5" />
              Download Report
            </button>
          )}

          {/* Ask a Question button */}
          {onAskQuestion && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onAskQuestion()
              }}
              className="px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              Ask a Question
            </button>
          )}

          {/* Quarter selector — stop propagation so clicking it doesn't toggle bar */}
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handlePreviousQuarter}
              className="p-1 hover:bg-muted rounded-md transition-colors"
              aria-label="Previous quarter"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
            </button>

            <div className="px-3 py-1.5 bg-muted/50 border border-border rounded-lg whitespace-nowrap">
              <span className="text-sm font-medium text-foreground">
                Q{selectedQuarter.quarter} {selectedQuarter.year}
              </span>
            </div>

            <button
              onClick={handleNextQuarter}
              className="p-1 hover:bg-muted rounded-md transition-colors"
              aria-label="Next quarter"
            >
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Live data notice — current quarter only ── */}
      {isCurrentQuarter && (
        <div
          className="flex items-center gap-2 px-4 py-2 bg-amber-50/60 dark:bg-amber-950/30 border-t border-amber-200/60 dark:border-amber-800/40"
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-[12px] text-amber-800 dark:text-amber-300 leading-snug">
            Outcome measure denominators update as patient assignments change throughout the quarter. Check back regularly to stay on track.
          </p>
        </div>
      )}

      {/* Expandable content panel */}
      <AnimatePresence initial={false}>
        {isExpanded && expandedContent && (
          <motion.div
            key="profile-expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
              opacity: { duration: 0.2, delay: 0.05 },
            }}
            className="overflow-hidden border-t border-border"
          >
            <div className="p-4">
              {expandedContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProviderProfileBar
